import { useAppDispatch, useAppSelector } from 'hooks/hooks'
import useAnalytics from 'hooks/useAnalytics'
import useNewEnrollment from 'hooks/useNewEnrollment'
import usePatientIntake from 'hooks/usePatientIntake'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getAccessToken } from 'reducers/authSlice'
import { setLoading } from 'reducers/tempSlice'
import styled from 'styled-components'
import Calendar from './Calendar/Calendar'
import Confirmation from './Confirmation/Confirmation'
import EnrollmentConfirmation from './Confirmation/EnrollmentConfirmation'
import FormCover from './FormCover/FormCover'
import CTLLifestyle from './Lifestyle/CTLLifestyle'
import PALifestyle from './Lifestyle/PALifestyle'
import MedicalBasics from './MedicalBasics/MedicalBasics'
import Medications from './Medications/Medications'
import NotEligible from './NotEligible/NotEligible'
import PAVsCTL from './PAVsCTL/PAVsCTL'
import Phone from './Phone/Phone'

const bothProgramMap: Record<number, number> = {
  4: 1, // started → step 1
  14: 2, // schedule → step 2
  11: 3, //phone
  5: 4,
  10: 5,
  6: 6, // physician → step 3
  7: 7, // meds → step 4
  8: 8, // lifestyle → step 5
  12: 9, // submitted → step 6 (EnrollmentConfirmation)
}

const bothProgramReverseMap: Record<number, number> = {
  1: 4, // step 1 → started
  2: 14, // step 2 → schedule
  3: 11, // Phone
  4: 5, //Confirmation
  5: 10, //start Enrollment
  6: 6, // step 3 → physician
  7: 7, // step 4 → meds
  8: 8, // step 5 → lifestyle
  9: 12, // step 6 → submitted
}

const oneProgramMap: Record<number, number> = {
  14: 1, // schedule → step 1
  11: 2, //phone
  5: 3,
  10: 4,
  6: 5, // physician → step 2
  7: 6, // meds → step 3
  8: 7, // lifestyle → step 4
  12: 8, // submitted → step 5
}

const oneProgramReverseMap: Record<number, number> = {
  1: 14, // step 1 → schedule
  2: 11, // Phone
  3: 5, //Confirmation
  4: 10, // Start Enrollment
  5: 6, // step 2 → physician
  6: 7, // step 3 → meds
  7: 8, // step 4 → lifestyle
  8: 12, // step 5 → submitted
}

export default function Enrollment() {
  const navigate = useNavigate()
  const accessToken = useAppSelector(getAccessToken)
  const { getEligibility, postEnrollmentForm } = useNewEnrollment({ token: accessToken })
  const { getEnrollmentStatus, postEnrollmentStatus } = usePatientIntake({
    token: accessToken,
  })
  const { postAnalyticsPageView } = useAnalytics(accessToken!)
  const dispatch = useAppDispatch()

  const [programEligibility, setProgramEligibility] = useState<string>('both')
  const [step, setStep] = useState<number>()
  const [selectedProgram, setSelectedProgram] = useState<number>(0) //0 = pa, 1 = ctl

  useEffect(() => {
    dispatch(setLoading(true))
    let eligibility: any = 'both'
    async function getEligibilityState() {
      eligibility = await getEligibility()
      await getEnrollmentStatus()
        .then((res) => {
          setStep(getStepFromStatus(res.data.statusId ?? 1, eligibility.data.eligibility))
        })
        .then(() => dispatch(setLoading(false)))
      setProgramEligibility(eligibility.data.eligibility)
      if (eligibility.data.chosenPath === 'ctl') {
        setSelectedProgram(1)
      }
    }
    getEligibilityState()
  }, [])

  useEffect(() => {
    window.scrollTo(0, 0)
    navigate(`/enrollment/${programEligibility}/${step ?? 1}`)
    if (step) {
      sendScreenView()
    }
  }, [step])

  const sendScreenView = () => {
    let stepName = ''

    if (programEligibility === 'both') {
      switch (step) {
        case 1:
          stepName = 'Choose Path'
          break
        case 2:
          stepName = 'Schedule'
          break
        case 3:
          stepName = 'Phone'
          break
        case 4:
          stepName = 'Appointment Confirmation'
          break
        case 5:
          stepName = 'Start Enrollment'
          break
        case 6:
          stepName = 'Medical Basics'
          break
        case 7:
          stepName = 'Medications'
          break
        case 8:
          stepName = 'LifeStyle'
          break
        case 9:
          stepName = 'Enrollment Confirmation'
          break
      }
      const screenName = `Enrollment: ${programEligibility} ${
        step && step >= 2 ? `Chosen path: ${selectedProgram === 1 ? 'ctl' : 'pa'}` : ''
      } step: ${step} (${stepName})`
      postAnalyticsPageView(screenName)
    } else {
      switch (step) {
        case 1:
          stepName = 'Schedule'
          break
        case 2:
          stepName = 'Phone'
          break
        case 3:
          stepName = 'Appointment Confirmation'
          break
        case 4:
          stepName = 'Start Enrollment'
          break
        case 5:
          stepName = 'Medical Basics'
          break
        case 6:
          stepName = 'Medications'
          break
        case 7:
          stepName = 'LifeStyle'
          break
        case 8:
          stepName = 'Enrollment Confirmation'
          break
      }
      const screenName = `Enrollment: ${programEligibility} step: ${step} (${stepName})`
      postAnalyticsPageView(screenName)
    }
  }

  function getStatusIdFromStep(step: number, programEligibility: string): number {
    if (programEligibility === 'both') {
      return bothProgramReverseMap[step] || 4 // default to 'started'
    } else {
      return oneProgramReverseMap[step] || 14 // default to 'schedule'
    }
  }

  function getStepFromStatus(statusId: number, programEligibility: string): number {
    if (programEligibility === 'both') {
      return bothProgramMap[statusId] ?? 1
    } else {
      return oneProgramMap[statusId] ?? 1
    }
  }

  const handleStepChange = (nextStep: number) => {
    setStep(nextStep)
    const statusId = getStatusIdFromStep(nextStep, programEligibility)
    const request = { statusId: statusId }
    postEnrollmentStatus(request)
  }
  const renderContent = () => {
    switch (step) {
      case 1:
        switch (programEligibility) {
          case 'none':
            return <NotEligible />
          case 'ctl':
            return <Calendar ctl={true} handleSubmit={() => handleStepChange(step + 1)} />
          case 'pa':
            return <Calendar ctl={false} handleSubmit={() => handleStepChange(step + 1)} />
          case 'both':
            return (
              <PAVsCTL
                handleSelectProgram={(program: number) => {
                  setSelectedProgram(program)
                  handleStepChange(2)
                }}
              />
            )
          default:
            return null
        }
      case 2:
        switch (programEligibility) {
          case 'none':
            return <NotEligible />
          case 'both':
            return <Calendar ctl={selectedProgram === 1} handleSubmit={() => handleStepChange(step + 1)} />
          case 'pa':
          case 'ctl':
            return <Phone ctl={programEligibility === 'ctl'} handleSubmit={() => handleStepChange(step + 1)} />
          default:
            return null
        }
      case 3:
        switch (programEligibility) {
          case 'none':
            return <NotEligible />
          case 'both':
            return <Phone ctl={selectedProgram === 1} handleSubmit={() => handleStepChange(step + 1)} />
          case 'pa':
          case 'ctl':
            return <Confirmation ctl={programEligibility === 'ctl'} handleNextStep={() => handleStepChange(step + 1)} />
          default:
            return null
        }
      case 4:
        switch (programEligibility) {
          case 'none':
            return <NotEligible />
          case 'both':
            return <Confirmation ctl={selectedProgram === 1} handleNextStep={() => handleStepChange(step + 1)} />
          case 'pa':
          case 'ctl':
            return <FormCover handleNextStep={() => handleStepChange(step + 1)} />
          default:
            return null
        }
      case 5:
        switch (programEligibility) {
          case 'none':
            return <NotEligible />
          case 'both':
            return <FormCover handleNextStep={() => handleStepChange(step + 1)} />
          case 'pa':
          case 'ctl':
            return <MedicalBasics backPressed={() => handleStepChange(step - 1)} onSubmit={() => handleStepChange(step + 1)} />
          default:
            return null
        }
      case 6:
        switch (programEligibility) {
          case 'none':
            return <NotEligible />
          case 'both':
            return <MedicalBasics backPressed={() => handleStepChange(step - 1)} onSubmit={() => handleStepChange(step + 1)} />
          case 'pa':
          case 'ctl':
            return <Medications backPressed={() => handleStepChange(step - 1)} onSubmit={() => handleStepChange(step + 1)} />
          default:
            return null
        }
      case 7:
        switch (programEligibility) {
          case 'none':
            return <NotEligible />
          case 'both':
            return <Medications backPressed={() => handleStepChange(step - 1)} onSubmit={() => handleStepChange(step + 1)} />
          case 'pa':
            return <PALifestyle backPressed={() => handleStepChange(step - 1)} handleSubmit={() => handleStepChange(step + 1)} />
          case 'ctl':
            return <CTLLifestyle ctl={true} backPressed={() => handleStepChange(step - 1)} handleSubmit={() => handleStepChange(step + 1)} />
          default:
            return null
        }
      case 8:
        switch (programEligibility) {
          case 'none':
            return <NotEligible />
          case 'both':
            return selectedProgram === 1 ? (
              <CTLLifestyle
                ctl={selectedProgram === 1}
                backPressed={() => handleStepChange(step - 1)}
                handleSubmit={() => handleStepChange(step + 1)}
              />
            ) : (
              <PALifestyle backPressed={() => handleStepChange(step - 1)} handleSubmit={() => handleStepChange(step + 1)} />
            )
          case 'pa':
            return <EnrollmentConfirmation ctl={false} />
          case 'ctl':
            return <EnrollmentConfirmation ctl={true} />
          default:
            return null
        }
      case 9:
        switch (programEligibility) {
          case 'both':
            return <EnrollmentConfirmation ctl={selectedProgram === 1} />
          default:
            return null
        }
      default:
        return null
    }
  }

  return <Container>{renderContent()}</Container>
}

const Container = styled.div`
  display: block;
  height: auto;
  @media only screen and (max-width: 600px) {
    margin-top: 20px;
  }
`
